/**
 * Elasticity Heatmap Calculations
 * Client-side computation of multipliers and premium gain % for screener results
 * across multiple underlying % change scenarios using Black-Scholes.
 */

import { blackScholes } from './blackScholes';
import {
  getColorForGainPct,
  getMultiplierColor,
} from '../theme/heatmapPalette';

// The rate every heatmap cell is priced at. Exported so anything explaining a
// cell (projected Greeks in the Details tab) reprices at the SAME rate — an
// explanation derived from a different r would disagree with the price it sits
// beside, which is the whole failure this layer is meant to avoid.
export const RISK_FREE_RATE = 0.05;

/**
 * All available scenario percentages for column toggles.
 * The sub-1% values (±0.5, ±1, ±2, ±3) were added for Intraday mode, where a
 * 0.5% underlying move is decision-relevant. They are inert for Basic/
 * Intermediate: their default sets reference none of them, and the settings
 * panel only surfaces them when the active mode opts in (see scenarioOptions).
 */
export const ALL_SCENARIOS = [-50, -35, -20, -10, -5, -3, -2.5, -2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2, 2.5, 3, 5, 10, 15, 20, 35, 50, 100, 200, 1000];

/**
 * Integer-only scenario list — the original set surfaced to Basic/Intermediate
 * so their Heatmap Settings toggle row is identical to before the intraday
 * change. Intraday passes ALL_SCENARIOS instead.
 */
export const STANDARD_SCENARIO_OPTIONS = [-50, -35, -20, -10, -5, 0, 5, 10, 15, 20, 35, 50, 100, 200, 1000];

/**
 * Default selected scenarios — focused 5-column set that captures the
 * most decision-relevant moves. Users can add more via the Heatmap Settings
 * sheet; their selection is persisted via getUserDefaultScenarios below.
 */
export const DEFAULT_SCENARIOS = new Set([-10, -5, 5, 10, 20]);

/**
 * Default selected scenarios for INTRADAY mode — fine-grained, centered on
 * small moves where short-dated premiums are most elastic.
 */
export const DEFAULT_SCENARIOS_INTRADAY = new Set([-3, -2.5, -2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2, 2.5, 3]);

const SCENARIOS_STORAGE_KEY = 'screener_heatmap_user_scenarios';
const SCENARIOS_STORAGE_KEY_INTRADAY = 'screener_heatmap_user_scenarios_intraday';

// Resolve the storage key + default set for a given screener mode. Default arg
// keeps every existing call site (Basic/Intermediate) on the global key.
function resolveScenarioConfig(mode = 'default') {
  if (mode === 'intraday') {
    return { storageKey: SCENARIOS_STORAGE_KEY_INTRADAY, defaultSet: DEFAULT_SCENARIOS_INTRADAY };
  }
  return { storageKey: SCENARIOS_STORAGE_KEY, defaultSet: DEFAULT_SCENARIOS };
}

/**
 * Read the user's persisted scenario set from localStorage, or fall back to
 * the shipped DEFAULT_SCENARIOS if nothing is saved (or the saved value is
 * malformed / empty).
 * @returns {Set<number>}
 */
export function getUserDefaultScenarios(mode = 'default') {
  const { storageKey, defaultSet } = resolveScenarioConfig(mode);
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return new Set(defaultSet);
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return new Set(defaultSet);
    return new Set(arr.map(Number).filter(n => !isNaN(n)));
  } catch {
    return new Set(defaultSet);
  }
}

/**
 * Persist the user's scenario selection so it becomes their default on the
 * next run / page reload. No-op if localStorage is unavailable.
 * Intraday persists under its own key so it never clobbers the global default
 * used by Basic/Intermediate.
 * @param {Set<number>} set
 * @param {string} mode - 'default' | 'intraday'
 */
export function saveUserDefaultScenarios(set, mode = 'default') {
  const { storageKey } = resolveScenarioConfig(mode);
  try {
    localStorage.setItem(storageKey, JSON.stringify(Array.from(set)));
  } catch {
    /* noop */
  }
}

/**
 * Clear the persisted scenario selection (used by the Heatmap Settings reset
 * action so that "Reset" goes back to the shipped default for that mode).
 * @param {string} mode - 'default' | 'intraday'
 */
export function clearUserDefaultScenarios(mode = 'default') {
  const { storageKey } = resolveScenarioConfig(mode);
  try {
    localStorage.removeItem(storageKey);
  } catch {
    /* noop */
  }
}

/**
 * Compute heatmap data for a single screener result row across all given scenarios.
 * @param {Object} row - Screener result row (must have Underlying Price, Strike, Days Until Expiration, Current Premium, Implied Volatility, Option Type, Position Type)
 * @param {number} daysToExit - Days until exit (from slider)
 * @param {number[]} scenarios - Array of scenario percentages (e.g., [-20, -10, 10, 20, ...])
 * @returns {Object} Map of scenarioPct -> { premiumGain, multiplier }
 */
/**
 * @param {Object} row - Screener result row
 * @param {number|null} daysToExit - Fixed days override, or null to use each row's own Days to Gain
 * @param {number[]} scenarios - Array of scenario percentages
 * @param {number|null} daysToExitPercent - Percentage of expiration (0-1), or null to ignore
 * @param {number|null} baselinePremium - Optional baseline override for premium gain %
 *   (used by the watchlist when the global baseline toggle is set to "avg_cost").
 *   Screener callers omit this and fall through to current premium.
 * @param {Object|null} forecast - Intraday-only forecasting refinements. Null (the
 *   default for every other mode / the watchlist) means the original behavior:
 *   sticky-strike IV, no event modeling. Shape:
 *     {
 *       ivSkew: { slope, curv } | null,   // sticky-moneyness smile (backend `_iv_skew`)
 *       stickyMoneyness: boolean,         // re-read IV at new moneyness as spot moves
 *       event: { in_window, diffusive_iv, days_to_earnings } | null,  // backend `_event`
 *       modelVolCrush: boolean,           // crush IV to the diffusive level past earnings
 *     }
 *   Both adjustments are anchored to the contract's own quoted IV so the 0%-move
 *   identity holds EXCEPT when a vol-crush is intentionally modeled (the 0% column
 *   then reflects the crush, which is the point).
 */
export function computeHeatmapRow(row, daysToExit, scenarios, daysToExitPercent = null, baselinePremium = null, forecast = null, intraday = null) {
  const underlyingPrice = parseFloat(row['Underlying Price']);
  const strike = parseFloat(row['Strike']);
  const daysUntilExp = parseFloat(row['Days Until Expiration']);
  const currentPremium = parseFloat(row['Current Premium']);
  const rawIV = parseFloat(row['Implied Volatility']);
  const optionType = row['Option Type']?.toLowerCase();
  const positionType = row['Position Type']?.toLowerCase();
  const isSell = positionType === 'sell';

  // Effective baseline for the premium-gain ratio. Falls back to current
  // premium when the override is missing or non-positive (toggle no-op).
  const effectiveBaseline = (Number.isFinite(baselinePremium) && baselinePremium > 0)
    ? baselinePremium
    : currentPremium;

  // Validate inputs
  if (!underlyingPrice || !strike || !daysUntilExp || !currentPremium || !optionType ||
      isNaN(underlyingPrice) || isNaN(strike) || isNaN(daysUntilExp) || isNaN(currentPremium)) {
    return {};
  }

  // Guard against very small premiums (avoids infinity)
  if (currentPremium < 0.01) {
    return {};
  }

  // IV: if > 5, assume it's in percentage form and convert to decimal
  const iv = rawIV > 5 ? rawIV / 100 : rawIV;
  if (!iv || isNaN(iv) || iv <= 0) {
    return {};
  }

  // Intraday uses a fractional time-to-expiry (sub-day) + finer near-expiry floor;
  // every other caller uses the integer Days Until Expiration and the standard floor.
  const dteDays = intraday && Number.isFinite(intraday.timeToExpiryDays) ? intraday.timeToExpiryDays : daysUntilExp;
  const floorYears = intraday && intraday.tFloorYears ? intraday.tFloorYears : 0.0001;

  // Determine effective days to exit based on mode:
  // 1. Percentage of expiration (per-row), 2. Fixed override, 3. Per-contract fallback
  let effectiveDaysToExit;
  if (daysToExitPercent !== null && daysToExitPercent !== undefined) {
    // Intraday: keep the sub-day fraction (don't round to whole days).
    effectiveDaysToExit = intraday
      ? dteDays * daysToExitPercent
      : Math.max(1, Math.round(daysUntilExp * daysToExitPercent));
  } else if (daysToExit !== null && daysToExit !== undefined) {
    effectiveDaysToExit = daysToExit;
  } else {
    effectiveDaysToExit = parseFloat(row['Days to Gain']) ||
      (intraday ? dteDays * 0.35 : Math.max(1, Math.round(daysUntilExp * 0.35)));
  }

  const timeToTarget = Math.max((dteDays - effectiveDaysToExit) / 365, floorYears);
  const result = {};

  // --- Intraday forecasting refinements (all no-ops when forecast is null) ---
  const ivSkew = forecast?.ivSkew || null;
  const ev = forecast?.event || null;

  // Vol-crush: if earnings is inside the contract's window AND the holding period
  // reaches at least to the earnings date, the projection is POST-event — IV
  // collapses to the diffusive baseline. This intentionally makes the 0% column
  // show a loss (the crush) rather than gain 0.
  const crushActive = !!(
    forecast?.modelVolCrush && ev && ev.in_window &&
    Number.isFinite(ev.diffusive_iv) && Number.isFinite(ev.days_to_earnings) &&
    effectiveDaysToExit >= ev.days_to_earnings
  );
  const baseIv = crushActive ? Math.max(0.01, ev.diffusive_iv) : iv;

  // Sticky-moneyness: the smile shifts IV as the contract's moneyness changes
  // with the underlying, anchored to baseIv at the current spot so the 0%-move
  // scenario reproduces baseIv exactly (preserves identity when not crushing).
  const applySkew = !!(forecast?.stickyMoneyness && ivSkew && Number.isFinite(ivSkew.slope));
  const skewSlope = applySkew ? ivSkew.slope : 0;
  const skewCurv = applySkew && Number.isFinite(ivSkew.curv) ? ivSkew.curv : 0;
  const x0 = applySkew ? Math.log(strike / underlyingPrice) : 0;
  const smileEval = (x) => skewSlope * x + skewCurv * x * x;

  for (const scenarioPct of scenarios) {
    const simulatedStock = underlyingPrice * (1 + scenarioPct / 100);

    // Ensure simulated stock is positive
    if (simulatedStock <= 0) {
      result[scenarioPct] = { premiumGain: null, multiplier: null, simulatedStock: null, projectedPremium: null };
      continue;
    }

    // Per-scenario IV: start from the (possibly crushed) base, then slide along
    // the smile if sticky-moneyness is on; sticky-strike holds it flat.
    let ivScenario = baseIv;
    if (applySkew) {
      const x1 = Math.log(strike / simulatedStock);
      ivScenario = Math.max(0.01, baseIv + (smileEval(x1) - smileEval(x0)));
    }

    const projectedPremium = blackScholes(
      simulatedStock, strike, timeToTarget, RISK_FREE_RATE, ivScenario, optionType
    );

    let premiumGain = 0;
    if (effectiveBaseline > 0) {
      if (isSell) {
        premiumGain = ((effectiveBaseline - projectedPremium) / effectiveBaseline) * 100;
      } else {
        premiumGain = ((projectedPremium - effectiveBaseline) / effectiveBaseline) * 100;
      }
    }

    // Multiplier = premium_gain / |scenario_pct|
    // Undefined for 0% scenario
    const multiplier = scenarioPct !== 0
      ? premiumGain / Math.abs(scenarioPct)
      : null;

    // The pricing inputs travel WITH the price. Anything that wants to explain
    // this cell — projected Greeks, an IV readout — must reprice from exactly
    // these, or it will quietly disagree with the premium shown beside it.
    // Additive: consumers that don't know these keys are unaffected.
    result[scenarioPct] = {
      premiumGain, multiplier, simulatedStock, projectedPremium,
      ivScenario, timeToTarget, baseIv, crushActive,
      strike, optionType, isSell,
      currentUnderlying: underlyingPrice,
      // Time remaining at the moment of quoting, i.e. before the holding period
      // is subtracted. The "now" half of any before/after comparison.
      timeNow: Math.max(dteDays / 365, floorYears),
      ivNow: iv,
    };
  }

  return result;
}

// --- Heatmap coloring ---
// Palette logic lives in theme/heatmapPalette.js so the watchlist heatmap and
// the scoring builder share one source of truth. This module re-exports the
// legacy `getHeatmapCellColor` surface so existing consumers keep working.

/**
 * Get heatmap cell color based on display mode
 * @param {number} value - The cell value (multiplier or premium gain %)
 * @param {string} displayMode - 'multiplier' or 'premiumGain'
 * @returns {Object} Style object with backgroundColor (and optionally color)
 */
export function getHeatmapCellColor(value, displayMode) {
  if (displayMode === 'multiplier') {
    return getMultiplierColor(value);
  }
  return getColorForGainPct(value);
}

// --- Formatting ---

/**
 * Format multiplier value (e.g., 3.5x)
 * @param {number} value
 * @returns {string}
 */
export function formatMultiplier(value) {
  if (value == null || isNaN(value) || value <= 0) return '--';
  return `${Number(value).toFixed(1)}x`;
}

/**
 * Format premium gain percentage.
 * Rules: 6 digits max, no decimals if >= 1000, commas included.
 * Under 1000: show 1 decimal (e.g., 345.2%)
 * 1000+: no decimals, commas (e.g., 12,345%)
 * @param {number} value
 * @returns {string}
 */
export function formatPremiumGain(value) {
  if (value == null || isNaN(value)) return '--';
  const num = Number(value);
  const abs = Math.abs(num);

  if (abs < 1000) {
    return `${num.toFixed(1)}%`;
  }
  // 1000+: no decimals, add commas
  const formatted = Math.round(num).toLocaleString('en-US');
  return `${formatted}%`;
}

/**
 * Format a scenario column header label
 * @param {number} scenarioPct - The scenario percentage (e.g., -20, 10, 200)
 * @returns {string} Formatted label (e.g., "-20%", "+10%", "0%")
 */
export function formatScenarioLabel(scenarioPct) {
  if (scenarioPct === 0) return '0%';
  return scenarioPct > 0 ? `+${scenarioPct}%` : `${scenarioPct}%`;
}

/**
 * The underlying price a scenario implies, e.g. -1% off a $658.92 base.
 *
 * Only meaningful when every row shares one underlying — the 0DTE module, which
 * analyses a single ticker. Returns null without a usable base price, which is
 * every other caller's signal to render the plain percentage alone.
 *
 * @param {number} scenarioPct - The scenario percentage (e.g. -20, 0.5, 10)
 * @param {number|null} basePrice - Current underlying price
 * @returns {string|null} Formatted price (e.g. "$652.31") or null
 */
export function formatScenarioPrice(scenarioPct, basePrice) {
  const base = Number(basePrice);
  if (!Number.isFinite(base) || base <= 0) return null;
  const price = base * (1 + scenarioPct / 100);
  if (!Number.isFinite(price)) return null;
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Single-string variant for contexts that can't render two lines — CSV headers
 * and the share-image column labels. Falls back to the plain label.
 * @returns {string} e.g. "+1% ($658.92)" or "+1%"
 */
export function formatScenarioLabelWithPrice(scenarioPct, basePrice) {
  const label = formatScenarioLabel(scenarioPct);
  const price = formatScenarioPrice(scenarioPct, basePrice);
  return price ? `${label} (${price})` : label;
}
